import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiEdit, FiUser, FiGitBranch, FiUsers, FiClipboard, FiCreditCard } from "react-icons/fi";
import { IoMdArrowBack } from "react-icons/io";
import { IoDocumentOutline } from "react-icons/io5";
import {
  MdClose,
  MdVerified,
  MdOutlineZoomInMap,
  MdOutlineZoomOutMap,
  MdOutlineVerifiedUser,
} from "react-icons/md";
import { FaUndoAlt, FaRedoAlt } from "react-icons/fa";
import { AiOutlineExclamationCircle, AiOutlineClose } from "react-icons/ai";
import { CSSTransition } from "react-transition-group";
import moment from "moment";

import ManageMCT from "../common/manageCT";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import { DeleteHospital, partnerVerification, getPartnerView } from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { showToast } from "../../../../reusableUI/toaster";
import PartnerDetails from "../../../../reusableUI/partnerdetails";
import Branches from "../common/branches";
import Visits from "../common/visits";
import Payouts from "../common/payouts";
import { getPartnerInfo } from "../../../../Service/RM";
import Pricing from "../partnerPortal/pricing";

const HospitalProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userID } = location.state || {};

  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({ title: "", url: "", type: "" });

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const [showVerifyWarning, setShowVerifyWarning] = useState(false);
  const [showDeclineWarning, setShowDeclineWarning] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [verifierName, setVerifierName] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [partnerID, setPartnerID] = useState(undefined);
  const [bookingsCount, setBookingsCount] = useState(0);
  const [hcpCount, setHcpCount] = useState(0);
  const [branchData, setBranchData] = useState([]);
  const [careTeamData, setCareTeamData] = useState([]);
  const [bookingsData, setBookingData] = useState([]);
  const [payoutMetrics, setPayoutMetrics] = useState([]);
  const [payoutTrans, setPayoutTrans] = useState([]);
  const [pricingData, setPricingData] = useState([]);

  const [numberVerificationModal, setNumberVerificationModal] = useState({
    show: false,
    type: "",
    action: "",
  });

  useEffect(() => {
    if (!userID) return;

    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      try {
        const result = await getPartnerInfo(userID);
        console.log("getPartnerInfo result:", result);

        if (cancelled) return;


        const partner =
          result?.data?.patrnerInfo ||
          result?.patrnerInfo ||
          result?.data ||
          null;

        const specialists =
          result?.data?.specialists ||
          result?.specialists ||
          [];

        const bookings =
          result?.data?.bookings ||
          result?.bookings ||
          [];

        setUser(partner);
        setPartnerID(partner?._id);
        setCareTeamData(specialists);
        setBranchData(partner?.branches);
        setBookingData(bookings);
        setPricingData(partner?.pricingInfo)
        setPayoutMetrics(result?.data?.metrics);
        setPayoutTrans(result?.data?.transactions)
        setHcpCount(Array.isArray(specialists) ? specialists.length : 0);
        setBookingsCount(Array.isArray(bookings) ? bookings.length : 0);
      } catch (err) {
        console.error("getPartnerInfo failed:", err);
        if (!cancelled) {
          setUser(null);
          setPartnerID(undefined);
          setHcpCount(0);
          setBookingsCount(0);
          showToast("Something went wrong while fetching user details.", "Error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();

    return () => {
      cancelled = true;
    };
  }, [userID]);

  const DeleteUser = async () => {
    const confirmed = window.confirm("Are you sure you want to delete this user?");
    if (!confirmed) return;

    try {
      const res = await DeleteHospital(user?._id);
      if (res?.error === false) {
        showToast("User Deleted Successfully", "Success");
        setTimeout(() => navigate("/partnerlist"), 1000);
      } else {
        showToast(res?.message || "Failed to delete user.", "Error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast("Something went wrong while deleting user.", "Error");
    }
  };

  const handleViewDocument = (title, url, type) => {
    setSelectedDocument({ title, url, type });
    setShowDocumentModal(true);
    setZoom(1);
    setRotation(0);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument({ title: "", url: "", type: "" });
    setZoom(1);
    setRotation(0);
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));
  const handleRotateLeft = () => setRotation((prev) => prev - 90);
  const handleRotateRight = () => setRotation((prev) => prev + 90);

  const getVerificationBadge = (verifiedStatus) => {
    if (verifiedStatus === "accepted") {
      return (
        <div className="flex flex-row items-center gap-1">
          <MdVerified color="#00AA44" size={14} />
          <span className="text-[#00AA44] text-[13px] font-poppins">Accepted</span>
        </div>
      );
    }
    if (verifiedStatus === "rejected") {
      return (
        <div className="flex flex-row items-center gap-1">
          <AiOutlineClose color="#D32F2F" size={14} />
          <span className="text-[#D32F2F] text-[13px] font-poppins">Rejected</span>
        </div>
      );
    }
    if (verifiedStatus === "pending") {
      return (
        <div className="flex flex-row items-center gap-1">
          <AiOutlineExclamationCircle color="#FFB900" size={14} />
          <span className="text-[#FFB900] text-[13px] font-poppins">Pending</span>
        </div>
      );
    }
    return <span className="text-gray-500 text-[13px] font-poppins">Not Provided</span>;
  };

  const handleDocumentVerification = async (verifiedStatus, actionBy, reason = "") => {
    try {
      const payload = {
        docType: selectedDocument.type,
        id: userID,
        action: verifiedStatus,
        verified: verifiedStatus === "accepted" ? "true" : "false",
        actionBy,
        reason,
      };

      const response = await partnerVerification(payload);
      if (response?.error === false) return { success: true, message: response.message };

      return { success: false, message: response?.message || "Verification failed" };
    } catch (error) {
      console.error("Verification API Error:", error);
      return { success: false, message: error?.message || "API request failed" };
    }
  };

  const handleVerify = () => setShowVerifyWarning(true);
  const handleDecline = () => setShowDeclineWarning(true);

  const confirmVerify = async () => {
    if (!verifierName.trim()) {
      showToast("Please enter your name", "Warning");
      return;
    }

    setIsVerifying(true);
    try {
      const result = await handleDocumentVerification(
        "accepted",
        verifierName,
        "Document verified successfully"
      );

      if (result.success) {
        showToast("Document verified successfully!", "Success");
        setShowVerifyWarning(false);
        setVerifierName("");
        handleCloseDocumentModal();
        await getPartnerInfo(userID);
      } else {
        showToast(result.message, "Error");
      }
    } catch (error) {
      console.error("Verification error:", error);
      showToast("Failed to verify document", "Error");
    } finally {
      setIsVerifying(false);
    }
  };

  const cancelVerify = () => {
    setShowVerifyWarning(false);
    setVerifierName("");
  };

  const confirmDecline = async () => {
    if (!verifierName.trim() || !declineReason.trim()) {
      showToast("Please enter your name and reason for declining", "Warning");
      return;
    }

    setIsDeclining(true);
    try {
      const result = await handleDocumentVerification("rejected", verifierName, declineReason);

      if (result.success) {
        showToast("Document rejected successfully", "Info");
        setShowDeclineWarning(false);
        setVerifierName("");
        setDeclineReason("");
        handleCloseDocumentModal();
        await getPartnerInfo(userID);
      } else {
        showToast(result.message, "Error");
      }
    } catch (error) {
      console.error("Decline error:", error);
      showToast("Failed to reject document", "Error");
    } finally {
      setIsDeclining(false);
    }
  };

  const cancelDecline = () => {
    setShowDeclineWarning(false);
    setVerifierName("");
    setDeclineReason("");
  };

  const handleNumberVerification = (type, action) => {
    setNumberVerificationModal({ show: true, type, action });
    setVerifierName("");
    setDeclineReason("");
  };

  const confirmNumberVerification = async () => {
    const { type, action } = numberVerificationModal;

    if (!verifierName.trim()) {
      showToast("Please enter your name", "Warning");
      return;
    }
    if (action === "rejected" && !declineReason.trim()) {
      showToast("Please provide a reason for rejecting", "Warning");
      return;
    }

    setIsVerifying(action === "accepted");
    setIsDeclining(action === "rejected");

    try {
      const payload = {
        docType: type,
        id: userID,
        verified: action === "accepted" ? "true" : "false",
        action,
        actionBy: verifierName,
        reason: action === "accepted" ? `${type} number verified successfully` : declineReason,
      };

      const response = await partnerVerification(payload);

      if (response?.error === false) {
        showToast(
          `${type} ${action === "accepted" ? "accepted" : "rejected"} successfully!`,
          action === "accepted" ? "Success" : "Info"
        );
        setNumberVerificationModal({ show: false, type: "", action: "" });
        setVerifierName("");
        setDeclineReason("");
        await getPartnerInfo(userID);
      } else {
        showToast(response?.message || `Failed to ${action} ${type}`, "Error");
      }
    } catch (error) {
      console.error("Number verification error:", error);
      showToast(`Failed to ${action} ${type}`, "Error");
    } finally {
      setIsVerifying(false);
      setIsDeclining(false);
    }
  };

  const cancelNumberVerification = () => {
    setNumberVerificationModal({ show: false, type: "", action: "" });
    setVerifierName("");
    setDeclineReason("");
  };

  const businessLicense = user?.verificationDocuments?.businessLicense || {};

  const getCurrentDocument = () => {
    if (selectedDocument.type === "businessLicense") return businessLicense;
    return {};
  };

  const currentDoc = getCurrentDocument();
  const isAccepted = currentDoc.verified === "accepted";
  const isRejected = currentDoc.verified === "rejected";

  console.log(pricingData)
  const renderProfileTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-[14px] border border-[#9EA3FF] shadow-md">
        <PartnerDetails user={user} page={"admin"} />
      </div>

      <div className="bg-white rounded-[14px] border border-[#9EA3FF] shadow-lg p-6">
        <div className="mb-6 pb-2 border-b border-gray-300">
          <h3 className="text-[14px] font-poppins font-semibold">Verification Documents</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* GST */}
          <div className="bg-gray-50 rounded-[8px] p-4">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-300">
              <p className="text-[#404040] text-[14px] font-poppins font-semibold">
                GST Certificate
              </p>
              {getVerificationBadge(user?.verificationDocuments?.GST?.verified)}
            </div>

            {user?.verificationDocuments?.GST?.number ? (
              <div className="flex flex-col gap-2">
                <div className="bg-[#e6f2fa] rounded-[8px] p-3">
                  <p className="text-[11px] text-gray-600 font-poppins mb-1">GST Number</p>
                  <p className="text-[13px] text-[#055aed] font-poppins font-semibold">
                    {user.verificationDocuments.GST.number}
                  </p>
                </div>

                <div className="mt-2 space-y-1">
                  {user.verificationDocuments.GST.uploadedAt && (
                    <p className="text-[12px] text-[#758edc] font-poppins">
                      Submitted on{" "}
                      {moment(user.verificationDocuments.GST.uploadedAt).format("DD MMM YYYY")}
                    </p>
                  )}
                  {user.verificationDocuments.GST.actionBy && (
                    <p className="text-[12px] text-[#758edc] font-poppins">
                      {user.verificationDocuments.GST.verified === "accepted"
                        ? "Accepted"
                        : "Rejected"}{" "}
                      by {capitalizeFirstLetter(user.verificationDocuments.GST.actionBy)}
                    </p>
                  )}
                  {user.verificationDocuments.GST.reason &&
                    user.verificationDocuments.GST.verified === "rejected" && (
                      <p className="text-[12px] text-red-600 font-poppins">
                        Reason: {user.verificationDocuments.GST.reason}
                      </p>
                    )}
                </div>

                {user?.verificationDocuments?.GST?.verified === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleNumberVerification("GST", "accepted")}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white py-1.5 rounded-md text-[12px] font-poppins"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleNumberVerification("GST", "rejected")}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white py-1.5 rounded-md text-[12px] font-poppins"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <p className="text-gray-500 text-[12px] font-poppins">No GST number provided</p>
              </div>
            )}
          </div>


          <div className="bg-gray-50 rounded-[8px] p-4">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-300">
              <p className="text-[#404040] text-[14px] font-poppins font-semibold">PAN Card</p>
              {getVerificationBadge(user?.verificationDocuments?.PAN?.verified)}
            </div>

            {user?.verificationDocuments?.PAN?.number ? (
              <div className="flex flex-col gap-2">
                <div className="bg-[#e6f2fa] rounded-[8px] p-3">
                  <p className="text-[11px] text-gray-600 font-poppins mb-1">PAN Number</p>
                  <p className="text-[13px] text-[#055aed] font-poppins font-semibold">
                    {user.verificationDocuments.PAN.number}
                  </p>
                </div>

                <div className="mt-2 space-y-1">
                  {user.verificationDocuments.PAN.uploadedAt && (
                    <p className="text-[12px] text-[#758edc] font-poppins">
                      Submitted on{" "}
                      {moment(user.verificationDocuments.PAN.uploadedAt).format("DD MMM YYYY")}
                    </p>
                  )}
                  {user.verificationDocuments.PAN.actionBy && (
                    <p className="text-[12px] text-[#758edc] font-poppins">
                      {user.verificationDocuments.PAN.verified === "accepted"
                        ? "Accepted"
                        : "Rejected"}{" "}
                      by {capitalizeFirstLetter(user.verificationDocuments.PAN.actionBy)}
                    </p>
                  )}
                  {user.verificationDocuments.PAN.reason &&
                    user.verificationDocuments.PAN.verified === "rejected" && (
                      <p className="text-[12px] text-red-600 font-poppins">
                        Reason: {user.verificationDocuments.PAN.reason}
                      </p>
                    )}
                </div>

                {user?.verificationDocuments?.PAN?.verified === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleNumberVerification("PAN", "accepted")}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white py-1.5 rounded-md text-[12px] font-poppins"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleNumberVerification("PAN", "rejected")}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white py-1.5 rounded-md text-[12px] font-poppins"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <p className="text-gray-500 text-[12px] font-poppins">No PAN number provided</p>
              </div>
            )}
          </div>


          <div className="bg-gray-50 rounded-[8px] p-4">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-300">
              <p className="text-[#404040] text-[14px] font-poppins font-semibold">
                Business License
              </p>
              {getVerificationBadge(businessLicense?.verified)}
            </div>

            {businessLicense?.docURL ? (
              <div className="flex flex-col gap-2">
                <div
                  className="bg-[#e6f2fa] rounded-[8px] p-2 flex items-center justify-center gap-2 cursor-pointer hover:bg-[#d0e7f7]"
                  onClick={() =>
                    handleViewDocument("Business License", businessLicense.docURL, "businessLicense")
                  }
                >
                  <IoDocumentOutline color="#055aed" size={16} />
                  <p className="text-[12px] text-[#055aed] font-poppins font-medium">
                    View Document
                  </p>
                </div>

                <div className="mt-2 space-y-1">
                  {businessLicense?.uploadedAt && (
                    <p className="text-[12px] text-[#758edc] font-poppins">
                      Uploaded on {moment(businessLicense.uploadedAt).format("DD MMM YYYY")}
                    </p>
                  )}
                  {businessLicense?.actionBy && (
                    <p className="text-[12px] text-[#758edc] font-poppins">
                      {businessLicense.verified === "accepted" ? "Accepted" : "Rejected"} by{" "}
                      {capitalizeFirstLetter(businessLicense.actionBy)}
                    </p>
                  )}
                  {businessLicense?.reason && businessLicense.verified === "rejected" && (
                    <p className="text-[12px] text-red-600 font-poppins">
                      Reason: {businessLicense.reason}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <p className="text-gray-500 text-[12px] font-poppins">No document uploaded</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCareTeamTab = () => <ManageMCT careTeamData={careTeamData} />;
  const renderBranches = () => <Branches branchData={branchData} />;
  const renderBookings = () => <Visits bookingsData={bookingsData} />;
  const renderPayments = () => <Payouts metricsData={payoutMetrics} transactionsData={payoutTrans} partnerInfoData={user} />;
  const renderPricing = () => <Pricing pricingData={pricingData} />;
  const renderTabContent = () => {
    switch (activeTab) {
      case "branches":
        return renderBranches();
      case "careTeam":
        return renderCareTeamTab();
      case "bookings":
        return renderBookings();
      case "payments":
        return renderPayments();
      case "pricing":
        return renderPricing();
      case "profile":
      default:
        return renderProfileTab();
    }
  };


  const tabCounts = {
    branches: user?.stats?.branches ?? user?.branches?.length ?? 0,
    careTeam: user?.stats?.careTeam ?? hcpCount ?? 0,
    bookings: user?.stats?.bookings ?? bookingsCount ?? 0,
  };


  const tabs = [
    { key: "profile", label: "Profile", icon: FiUser },
    { key: "branches", label: "Branches", icon: FiGitBranch, count: tabCounts.branches },
    { key: "careTeam", label: "Care Team", icon: FiUsers, count: tabCounts.careTeam },
    { key: "bookings", label: "Bookings", icon: FiClipboard, count: tabCounts.bookings },

    { key: "payments", label: "Payments", icon: FiCreditCard },
    { key: "pricing", label: "Service & Pricing", icon: FiUser },
  ];

  const back = localStorage.getItem("profilePage");

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <Loader loading={loading} />
      </div>
    );
  }

  return (

    <div className="h-[100dvh] bg-[#F5F7FB] overflow-hidden">



      <div className="h-full overflow-y-auto overscroll-contain px-2 pb-6">
        {/* Sticky header + tabs */}
        <div className="sticky top-0 z-20 bg-[#F5F7FB] pt-2">
          {/* Top Header */}
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="flex items-center gap-3">
              <div
                className="cursor-pointer hover:bg-gray-200 rounded-full p-2 transition-colors"
                onClick={() => navigate(back !== "userProfile" ? "/partnerlist" : "/user")}
              >
                <IoMdArrowBack size={20} color="#111827" />
              </div>

              <div>
                <h2 className="text-[22px] font-poppins font-semibold text-[#1F2937]">
                  {capitalizeFirstLetter(user?.basicInfo?.name || "Hospital Profile")}
                </h2>
              </div>
            </div>

            {activeTab === "profile" && (
              <div className="flex gap-2">
                <button
                  className="border border-[#4951EC] text-[#4951EC] hover:bg-[#EEF2FF] font-poppins text-[12px] px-3 py-1 rounded-lg flex items-center gap-1 transition-colors"
                  onClick={() => {
                    localStorage.setItem("profilePage", "hospital");
                    navigate("/partnerEdit", { state: { editUser: user } });
                  }}
                >
                  <FiEdit size={14} />
                  Edit
                </button>
                <button
                  className="bg-[#EF4444] text-white text-[12px] font-poppins px-3 py-1 rounded-lg hover:bg-[#DC2626] transition-colors"
                  onClick={DeleteUser}
                >
                  Delete
                </button>
              </div>
            )}
          </div>


          <div className="px-2 mb-4">
            <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm">
              <div className="flex items-center gap-2 overflow-x-auto flex-wrap px-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;

                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={[
                        "relative flex items-center gap-2 px-4 py-3 font-poppins text-[13px] transition-colors",
                        "border-b-2",
                        isActive
                          ? "text-[#1D4ED8] border-[#1D4ED8]"
                          : "text-[#6B7280] border-transparent hover:text-[#111827]",
                      ].join(" ")}
                    >
                      <Icon size={14} className={isActive ? "text-[#1D4ED8]" : "text-[#6B7280]"} />
                      <span className="font-medium">{tab.label}</span>


                      {tab.count !== undefined && tab.count !== null && (
                        <span
                          className={[
                            "ml-1 inline-flex items-center justify-center min-w-[26px] h-[18px] px-2 rounded-full text-[11px] font-semibold",
                            isActive ? "bg-[#1D4ED8] text-white" : "bg-[#EEF2FF] text-[#1D4ED8]",
                          ].join(" ")}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>


        <div className="px-2 pb-10">{renderTabContent()}</div>
      </div>


      <CSSTransition in={showDocumentModal} timeout={300} classNames="modal" unmountOnExit>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-black rounded-lg w-full md:w-[80%] lg:w-[60%] xl:w-[50%] max-h-[90vh] p-4 overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <MdOutlineVerifiedUser className="text-blue-500" size={22} />
                <h3 className="font-poppins text-[16px] font-semibold text-white">
                  {selectedDocument.title}
                </h3>

                <div className="ml-2">
                  {isAccepted ? (
                    <span className="text-green-500 text-[13px] font-poppins flex items-center gap-1">
                      <MdVerified size={16} />
                      Accepted
                    </span>
                  ) : isRejected ? (
                    <span className="text-red-500 text-[13px] font-poppins flex items-center gap-1">
                      <AiOutlineClose size={16} />
                      Rejected
                    </span>
                  ) : (
                    <span className="text-yellow-500 text-[13px] font-poppins flex items-center gap-1">
                      <AiOutlineExclamationCircle size={16} />
                      Pending
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleZoomIn}
                  className="p-2 rounded-lg text-white text-xs hover:bg-gray-700"
                >
                  <div className="flex items-center gap-1">
                    <MdOutlineZoomInMap size={16} />
                    Zoom In
                  </div>
                </button>

                <button
                  onClick={handleZoomOut}
                  className="p-2 rounded-lg text-white text-xs hover:bg-gray-700"
                >
                  <div className="flex items-center gap-1">
                    Zoom Out
                    <MdOutlineZoomOutMap size={16} />
                  </div>
                </button>

                <button
                  onClick={handleCloseDocumentModal}
                  className="text-white hover:bg-gray-700 rounded-full p-1"
                >
                  <MdClose size={24} />
                </button>
              </div>
            </div>

            <div className="border-b border-gray-600 mb-4" />

            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={handleRotateLeft}
                className="rounded-lg border border-gray-300 text-white text-xs py-1 px-2 hover:bg-gray-700"
              >
                <div className="flex items-center gap-1">
                  <FaUndoAlt size={12} />
                  <p>Rotate Left</p>
                </div>
              </button>

              <button
                onClick={handleRotateRight}
                className="rounded-lg border border-gray-300 text-white text-xs py-1 px-2 hover:bg-gray-700"
              >
                <div className="flex items-center gap-1">
                  <FaRedoAlt size={12} />
                  <p>Rotate Right</p>
                </div>
              </button>
            </div>

            <div className="flex justify-center mb-4 overflow-hidden" style={{ maxHeight: "50vh" }}>
              {selectedDocument.url ? (
                selectedDocument.url.toLowerCase().endsWith(".pdf") ? (
                  <iframe
                    src={selectedDocument.url}
                    className="border-0 rounded"
                    title={selectedDocument.title}
                    style={{
                      transform: `scale(${zoom}) rotate(${rotation}deg)`,
                      transition: "transform 0.3s ease",
                      width: "100%",
                      height: "500px",
                    }}
                  />
                ) : (
                  <img
                    src={selectedDocument.url}
                    alt={selectedDocument.title}
                    className="rounded"
                    style={{
                      transform: `scale(${zoom}) rotate(${rotation}deg)`,
                      transition: "transform 0.3s ease",
                      maxWidth: "100%",
                      maxHeight: "400px",
                      objectFit: "contain",
                    }}
                  />
                )
              ) : (
                <div className="flex items-center justify-center h-64">
                  <p className="text-gray-400 font-poppins">No document available</p>
                </div>
              )}
            </div>

            {selectedDocument.url && currentDoc.verified === "pending" && (
              <div className="flex justify-center gap-3 mt-4">
                <button
                  className="bg-red-600 hover:bg-red-700 rounded-2xl px-6 py-2 text-xs"
                  onClick={() => setShowDeclineWarning(true)}
                  disabled={isDeclining}
                >
                  <p className="text-white font-poppins">Reject</p>
                </button>

                <button
                  className="bg-green-500 hover:bg-green-600 rounded-2xl px-6 py-2 text-xs"
                  onClick={() => setShowVerifyWarning(true)}
                  disabled={isVerifying}
                >
                  <p className="text-white font-poppins">Accept</p>
                </button>
              </div>
            )}
          </div>
        </div>
      </CSSTransition>


      {showVerifyWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-center text-xl font-bold mb-4 font-poppins">Are you sure?</h3>
            <p className="text-center mb-6 font-poppins text-gray-700">
              Do you want to accept this document?
            </p>

            <input
              className="w-full p-2 border border-gray-300 rounded-md mb-4 font-poppins text-sm"
              placeholder="Enter your name"
              value={verifierName}
              onChange={(e) => setVerifierName(e.target.value)}
            />

            <div className="flex justify-between gap-3">
              <button
                className="bg-gray-300 hover:bg-gray-400 rounded-lg px-4 py-2 text-sm font-poppins flex-1"
                onClick={cancelVerify}
              >
                Cancel
              </button>

              <button
                className={`rounded-lg px-4 py-2 text-sm font-poppins flex-1 ${isVerifying || !verifierName.trim()
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600 text-white"
                  }`}
                onClick={confirmVerify}
                disabled={isVerifying || !verifierName.trim()}
              >
                {isVerifying ? "Processing..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-center text-xl font-bold mb-4 font-poppins">Are you sure?</h3>
            <p className="text-center mb-6 font-poppins text-gray-700">
              Do you want to reject this document?
            </p>

            <input
              className="w-full p-2 border border-gray-300 rounded-md mb-4 font-poppins text-sm"
              placeholder="Enter your name"
              value={verifierName}
              onChange={(e) => setVerifierName(e.target.value)}
            />

            <textarea
              className="w-full p-2 border border-gray-300 rounded-md mb-4 font-poppins text-sm"
              rows="4"
              placeholder="Please provide a reason for rejecting"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
            />

            <div className="flex justify-between gap-3">
              <button
                className="bg-gray-300 hover:bg-gray-400 rounded-lg px-4 py-2 text-sm font-poppins flex-1"
                onClick={cancelDecline}
              >
                Cancel
              </button>

              <button
                className={`rounded-lg px-4 py-2 text-sm font-poppins flex-1 ${isDeclining || !verifierName.trim() || !declineReason.trim()
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                onClick={confirmDecline}
                disabled={isDeclining || !verifierName.trim() || !declineReason.trim()}
              >
                {isDeclining ? "Processing..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Number Verification Modal */}
      {numberVerificationModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-center text-xl font-bold mb-4 font-poppins">
              {numberVerificationModal.action === "accepted" ? "Accept" : "Reject"}{" "}
              {numberVerificationModal.type}?
            </h3>

            <input
              className="w-full p-2 border border-gray-300 rounded-md mb-4 font-poppins text-sm"
              placeholder="Enter your name"
              value={verifierName}
              onChange={(e) => setVerifierName(e.target.value)}
            />

            {numberVerificationModal.action === "rejected" && (
              <textarea
                className="w-full p-2 border border-gray-300 rounded-md mb-4 font-poppins text-sm"
                rows="4"
                placeholder="Please provide a reason for rejecting"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
              />
            )}

            <div className="flex justify-between gap-3">
              <button
                className="bg-gray-300 hover:bg-gray-400 rounded-lg px-4 py-2 text-sm font-poppins flex-1"
                onClick={cancelNumberVerification}
              >
                Cancel
              </button>

              <button
                className={`rounded-lg px-4 py-2 text-sm font-poppins flex-1 ${isVerifying ||
                  isDeclining ||
                  !verifierName.trim() ||
                  (numberVerificationModal.action === "rejected" && !declineReason.trim())
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : numberVerificationModal.action === "accepted"
                    ? "bg-green-500 hover:bg-green-600 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                onClick={confirmNumberVerification}
                disabled={
                  isVerifying ||
                  isDeclining ||
                  !verifierName.trim() ||
                  (numberVerificationModal.action === "rejected" && !declineReason.trim())
                }
              >
                {isVerifying || isDeclining ? "Processing..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalProfile;